import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Rating, Avatar, Divider, Slider,
} from '@mui/material';
import { ExitToApp, Add, Edit, Close, Visibility, CheckCircle, Cancel as CancelIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  scheduled: { color: 'warning', label: 'Scheduled' },
  completed: { color: 'success', label: 'Completed' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const REASON_LABELS: Record<string, string> = {
  better_opportunity: '🚀 Better Opportunity',
  compensation:       '💰 Compensation',
  work_life_balance:  '⚖️ Work-Life Balance',
  management:         '👤 Management',
  career_growth:      '📈 Career Growth',
  relocation:         '📍 Relocation',
  personal:           '❤️ Personal',
  retirement:         '🏖️ Retirement',
  company_culture:    '🏢 Company Culture',
  other:              '❓ Other',
};

const SAT_KEYS = [
  { key: 'satisfactionRole',           label: 'Role & Work' },
  { key: 'satisfactionCompensation',   label: 'Compensation' },
  { key: 'satisfactionManagement',     label: 'Management' },
  { key: 'satisfactionCulture',        label: 'Culture' },
  { key: 'satisfactionWorkLifeBalance', label: 'Work-Life Balance' },
  { key: 'satisfactionCareerGrowth',   label: 'Career Growth' },
];

interface ExitInterview { id: string; employeeId: string; interviewerId?: string; status: string; scheduledDate?: string; lastWorkingDay?: string; primaryReason?: string; primaryReasonDetail?: string; satisfactionRole?: number; satisfactionCompensation?: number; satisfactionManagement?: number; satisfactionCulture?: number; satisfactionWorkLifeBalance?: number; satisfactionCareerGrowth?: number; whatWorkedWell?: string; whatCouldImprove?: string; wouldRecommendCompany?: string; additionalComments?: string; isConfidential?: boolean; hrNotes?: string; openToRehire?: boolean; employee?: { firstName: string; lastName: string; department?: string; jobTitle?: string }; interviewer?: { firstName: string; lastName: string }; }

function ScoreBar({ label, value }: { label: string; value: number }) {
  const pct = (value / 5) * 100;
  const color = value >= 4 ? '#2e7d32' : value >= 3 ? '#f57c00' : '#c62828';
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="caption" fontWeight={700} color={color}>{value.toFixed(1)}/5</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );
}

export default function ExitInterviews() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [list, setList] = useState<ExitInterview[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scheduleDialog, setScheduleDialog] = useState<Partial<ExitInterview> | null>(null);
  const [conductDialog, setConductDialog] = useState<{ interview: ExitInterview; form: Partial<ExitInterview> } | null>(null);
  const [viewDialog, setViewDialog] = useState<ExitInterview | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [data, st] = await Promise.all([
        apiFetch(`/api/exit-interviews${statusFilter ? `?status=${statusFilter}` : ''}`),
        apiFetch('/api/exit-interviews/stats'),
      ]);
      setList(data || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    if (!scheduleDialog) return;
    try {
      await apiFetch('/api/exit-interviews', { method: 'POST', body: JSON.stringify(scheduleDialog) });
      setScheduleDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const conduct = async () => {
    if (!conductDialog) return;
    try {
      await apiFetch(`/api/exit-interviews/${conductDialog.interview.id}/complete`,
        { method: 'PATCH', body: JSON.stringify(conductDialog.form) });
      setConductDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancelInterview = async (id: string) => {
    try { await apiFetch(`/api/exit-interviews/${id}/cancel`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExitToApp sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Exit Interviews</Typography>
            <Typography variant="body2" color="text.secondary">Capture insights from departing employees</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />}
            onClick={() => setScheduleDialog({ status: 'scheduled', isConfidential: true })}
            sx={{ bgcolor: '#1a237e' }}>
            Schedule Interview
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{stats.total}</Typography>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#e65100">{stats.scheduled}</Typography>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{stats.rehireRate}%</Typography>
                <Typography variant="caption" color="text.secondary">Open to Rehire</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#4a148c">{stats.recommendRate}%</Typography>
                <Typography variant="caption" color="text.secondary">Would Recommend</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Insights row */}
      {stats && stats.total > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1.5 }}>Top Reasons for Leaving</Typography>
              {stats.topReasons.map((r: any) => (
                <Box key={r.reason} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography variant="body2">{REASON_LABELS[r.reason] ?? r.reason}</Typography>
                  <Chip label={r.count} size="small" />
                </Box>
              ))}
              {stats.topReasons.length === 0 && <Typography color="text.secondary" variant="body2">No data yet.</Typography>}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1.5 }}>Average Satisfaction Scores</Typography>
              {SAT_KEYS.map(k => (
                <ScoreBar key={k.key} label={k.label} value={stats.avgSatisfaction[k.key.replace('satisfaction', '').charAt(0).toLowerCase() + k.key.replace('satisfaction', '').slice(1)] ?? stats.avgSatisfaction[k.key] ?? 0} />
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="All Interviews" />
        </Tabs>
      </Paper>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {['', 'scheduled', 'completed', 'cancelled'].map(s => (
          <Chip key={s} label={s || 'All'} onClick={() => setStatusFilter(s)}
            color={statusFilter === s ? 'primary' : 'default'} variant={statusFilter === s ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><b>Employee</b></TableCell>
              <TableCell><b>Last Day</b></TableCell>
              <TableCell><b>Interview Date</b></TableCell>
              <TableCell><b>Reason</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Rehire</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(e => (
              <TableRow key={e.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: '#1a237e', fontSize: '0.75rem' }}>
                      {e.employee?.firstName?.[0]}{e.employee?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{e.employee?.firstName} {e.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{e.employee?.department}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{e.lastWorkingDay ? new Date(e.lastWorkingDay + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short' }) : '—'}</TableCell>
                <TableCell>{e.scheduledDate ? new Date(e.scheduledDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short' }) : '—'}</TableCell>
                <TableCell>{e.primaryReason ? REASON_LABELS[e.primaryReason] : '—'}</TableCell>
                <TableCell><Chip label={STATUS_CFG[e.status]?.label ?? e.status} size="small" color={STATUS_CFG[e.status]?.color} /></TableCell>
                <TableCell>
                  {e.openToRehire === true ? '✅' : e.openToRehire === false ? '❌' : '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => setViewDialog(e)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    {isHR && e.status === 'scheduled' && (
                      <Tooltip title="Conduct Interview">
                        <IconButton size="small" color="success"
                          onClick={() => setConductDialog({ interview: e, form: { ...e } })}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isHR && e.status === 'scheduled' && (
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => cancelInterview(e.id)}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No exit interviews.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── SCHEDULE DIALOG ── */}
      <Dialog open={!!scheduleDialog} onClose={() => setScheduleDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Schedule Exit Interview
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setScheduleDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Employee ID" fullWidth required value={scheduleDialog?.employeeId ?? ''}
              onChange={e => setScheduleDialog(p => ({ ...p, employeeId: e.target.value }))} />
            <TextField label="Last Working Day" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={scheduleDialog?.lastWorkingDay ?? ''} onChange={e => setScheduleDialog(p => ({ ...p, lastWorkingDay: e.target.value }))} />
            <TextField label="Interview Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={scheduleDialog?.scheduledDate ?? ''} onChange={e => setScheduleDialog(p => ({ ...p, scheduledDate: e.target.value }))} />
            <TextField label="Interviewer ID (optional)" fullWidth value={scheduleDialog?.interviewerId ?? ''}
              onChange={e => setScheduleDialog(p => ({ ...p, interviewerId: e.target.value || undefined }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={schedule} disabled={!scheduleDialog?.employeeId}>Schedule</Button>
        </DialogActions>
      </Dialog>

      {/* ── CONDUCT INTERVIEW DIALOG ── */}
      <Dialog open={!!conductDialog} onClose={() => setConductDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Conduct Exit Interview — {conductDialog?.interview.employee?.firstName} {conductDialog?.interview.employee?.lastName}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setConductDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Primary Reason for Leaving" select fullWidth
                  value={conductDialog?.form.primaryReason ?? ''}
                  onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, primaryReason: e.target.value as any } } : null)}>
                  {Object.entries(REASON_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Would Recommend Company?" select fullWidth
                  value={conductDialog?.form.wouldRecommendCompany ?? ''}
                  onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, wouldRecommendCompany: e.target.value } } : null)}>
                  <MenuItem value="yes">✅ Yes</MenuItem>
                  <MenuItem value="no">❌ No</MenuItem>
                  <MenuItem value="maybe">🤔 Maybe</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Reason Details" fullWidth multiline rows={2}
              value={conductDialog?.form.primaryReasonDetail ?? ''}
              onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, primaryReasonDetail: e.target.value } } : null)} />

            <Divider><Typography variant="caption">Satisfaction Scores (1–5)</Typography></Divider>
            <Grid container spacing={2}>
              {SAT_KEYS.map(k => (
                <Grid key={k.key} size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" gutterBottom>{k.label}: <b>{(conductDialog?.form as any)?.[k.key] ?? '—'}</b></Typography>
                  <Slider min={1} max={5} step={1} marks value={(conductDialog?.form as any)?.[k.key] ?? 3}
                    onChange={(_, v) => setConductDialog(p => p ? { ...p, form: { ...p.form, [k.key]: v } } : null)}
                    sx={{ color: '#1a237e' }} />
                </Grid>
              ))}
            </Grid>

            <Divider><Typography variant="caption">Open Feedback</Typography></Divider>
            <TextField label="What Worked Well?" fullWidth multiline rows={2}
              value={conductDialog?.form.whatWorkedWell ?? ''}
              onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, whatWorkedWell: e.target.value } } : null)} />
            <TextField label="What Could Improve?" fullWidth multiline rows={2}
              value={conductDialog?.form.whatCouldImprove ?? ''}
              onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, whatCouldImprove: e.target.value } } : null)} />
            <TextField label="Additional Comments" fullWidth multiline rows={2}
              value={conductDialog?.form.additionalComments ?? ''}
              onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, additionalComments: e.target.value } } : null)} />

            <Divider><Typography variant="caption">HR Notes (confidential)</Typography></Divider>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Open to Rehire?" select fullWidth
                  value={conductDialog?.form.openToRehire === true ? 'yes' : conductDialog?.form.openToRehire === false ? 'no' : ''}
                  onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, openToRehire: e.target.value === 'yes' } } : null)}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField label="HR Internal Notes" fullWidth multiline rows={2}
              value={conductDialog?.form.hrNotes ?? ''}
              onChange={e => setConductDialog(p => p ? { ...p, form: { ...p.form, hrNotes: e.target.value } } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConductDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={conduct}>Mark Completed</Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW DIALOG ── */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Exit Interview — {viewDialog?.employee?.firstName} {viewDialog?.employee?.lastName}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setViewDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip label={STATUS_CFG[viewDialog?.status ?? '']?.label ?? viewDialog?.status} size="small" color={STATUS_CFG[viewDialog?.status ?? '']?.color} />
              {viewDialog?.isConfidential && <Chip label="🔒 Confidential" size="small" />}
            </Box>
            <Typography><b>Last Working Day:</b> {viewDialog?.lastWorkingDay ?? '—'}</Typography>
            <Typography><b>Primary Reason:</b> {viewDialog?.primaryReason ? REASON_LABELS[viewDialog.primaryReason] : '—'}</Typography>
            {viewDialog?.primaryReasonDetail && <Typography><b>Details:</b> {viewDialog.primaryReasonDetail}</Typography>}
            {viewDialog?.status === 'completed' && (
              <>
                <Divider />
                <Typography fontWeight={700}>Satisfaction Scores</Typography>
                {SAT_KEYS.filter(k => (viewDialog as any)[k.key]).map(k => (
                  <Box key={k.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{k.label}</Typography>
                    <Rating value={(viewDialog as any)[k.key]} max={5} readOnly size="small" />
                  </Box>
                ))}
                <Divider />
                {viewDialog?.whatWorkedWell && <Box><Typography variant="caption" color="text.secondary">What Worked Well</Typography><Typography variant="body2">{viewDialog.whatWorkedWell}</Typography></Box>}
                {viewDialog?.whatCouldImprove && <Box><Typography variant="caption" color="text.secondary">What Could Improve</Typography><Typography variant="body2">{viewDialog.whatCouldImprove}</Typography></Box>}
                <Typography><b>Would Recommend:</b> {viewDialog?.wouldRecommendCompany ?? '—'}</Typography>
                <Typography><b>Open to Rehire:</b> {viewDialog?.openToRehire === true ? 'Yes ✅' : viewDialog?.openToRehire === false ? 'No ❌' : '—'}</Typography>
                {isHR && viewDialog?.hrNotes && (
                  <Box sx={{ bgcolor: '#fff3e0', p: 1, borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">HR Notes</Typography>
                    <Typography variant="body2">{viewDialog.hrNotes}</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setViewDialog(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
